import React from 'react'
import { Dialog } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { DetailButton } from '../common/detail'
import '../common/detail/detail.css'
import '../common/form/form.css'
import './QuickCreate.css'

type QuickCreateDialogProps = {
  title: string
  subtitle?: string
  busy?: boolean
  error?: string | null
  submitLabel: string
  onSubmit: () => void
  onClose: () => void
  children: React.ReactNode
}

/**
 * Modal used by every quick-create form. Dialogs opened from inside another
 * one stack on top of it, so a pipeline can be created from a project form.
 */
export default function QuickCreateDialog({
  title,
  subtitle,
  busy,
  error,
  submitLabel,
  onSubmit,
  onClose,
  children,
}: QuickCreateDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog
      open
      onClose={(_, reason) => {
        if (busy || reason === 'backdropClick') return
        onClose()
      }}
      maxWidth={false}
      PaperProps={{ className: 'incyia-modal-paper qc-paper' }}
      aria-labelledby="qc-title"
    >
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onSubmit()
        }}
      >
        <div className="incyia-modal-head">
          <div>
            <h3 id="qc-title">{title}</h3>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button
            type="button"
            className="incyia-modal-x"
            onClick={onClose}
            disabled={busy}
            aria-label={t('buttons.cancel', { defaultValue: 'Cancel' })}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>
        <div className="incyia-modal-body">
          {error ? (
            <div className="qc-error" role="alert">
              {error}
            </div>
          ) : null}
          {children}
        </div>
        <div className="incyia-modal-foot">
          <DetailButton type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {t('buttons.cancel', { defaultValue: 'Cancel' })}
          </DetailButton>
          <DetailButton type="submit" disabled={busy}>
            {busy ? t('quickCreate.creating') : submitLabel}
          </DetailButton>
        </div>
      </form>
    </Dialog>
  )
}
